/** SUNTRONIX ONE — EMPLOYEE HUB (external self-service).
 *  Mobile-first, action-first: My Devices, My Deliveries, My Support.
 *  Employee-scoped over existing records; raising a request reuses the
 *  Service Desk portal endpoint. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Laptop, Truck, LifeBuoy, Loader2, Plus, X, ShieldCheck, PackageCheck,
  ChevronRight, CalendarClock,
} from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

export default function EmployeeHub() {
  const [tab, setTab] = useState("home");
  const [home, setHome] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/employee-hub/home").then(({ data }) => setHome(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load your hub"));
  }, []);

  if (err) return <div className="p-6 text-sm text-[color:var(--st-text-muted)]" data-testid="employee-hub-error">{err}</div>;
  if (!home) return <div className="p-6 flex items-center gap-2 text-sm text-[color:var(--st-text-subtle)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading your hub…</div>;

  const tabs = [
    { key: "home", label: "Home" },
    { key: "devices", label: "My Devices" },
    { key: "deliveries", label: "My Deliveries" },
    { key: "support", label: "My Support" },
    { key: "documents", label: "My Documents" },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="employee-hub">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] rounded-full bg-[color:var(--st-surface-alt)] px-2.5 py-1 text-[color:var(--st-text-muted)]">
          <ShieldCheck className="h-3.5 w-3.5" /> Your personal workspace
        </div>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-[color:var(--st-text)]">Hi {home.employee.name?.split(" ")[0] || "there"} 👋</h1>
        <p className="text-sm text-[color:var(--st-text-muted)]">Everything about your devices, deliveries and support in one place.</p>
      </div>

      <div className="flex gap-1.5 overflow-x-auto border-b border-[color:var(--st-border)] pb-2">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`emp-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={"whitespace-nowrap px-3 py-1.5 rounded-md text-[13px] font-medium " +
              (tab === t.key ? "bg-[color:var(--st-text)] text-white" : "text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)]")}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "home" && <Home home={home} onGo={setTab} />}
      {tab === "devices" && <Devices />}
      {tab === "deliveries" && <Deliveries />}
      {tab === "support" && <Support employeeId={home.employee.id} tenantId={home.employee.company_id} />}
      {tab === "documents" && <Documents />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-left rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4 hover:border-[color:var(--st-accent)] transition-colors">
      <div className="flex items-center justify-between">
        <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: "color-mix(in srgb, var(--st-accent) 12%, transparent)", color: "var(--st-accent)" }}>
          <Icon className="h-5 w-5" strokeWidth={1.7} />
        </div>
        <ChevronRight className="h-4 w-4 text-[color:var(--st-text-subtle)]" />
      </div>
      <div className="mt-3 text-2xl font-semibold text-[color:var(--st-text)]">{value}</div>
      <div className="text-[13px] text-[color:var(--st-text-muted)]">{label}</div>
    </button>
  );
}

function Home({ home, onGo }) {
  const c = home.counts;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Laptop} label="My devices" value={c.devices} onClick={() => onGo("devices")} testid="emp-stat-devices" />
        <StatCard icon={Truck} label={c.in_transit ? `Deliveries (${c.in_transit} in transit)` : "My deliveries"} value={c.deliveries} onClick={() => onGo("deliveries")} testid="emp-stat-deliveries" />
        <StatCard icon={LifeBuoy} label="Open requests" value={c.open_requests} onClick={() => onGo("support")} testid="emp-stat-requests" />
        <StatCard icon={PackageCheck} label="In transit" value={c.in_transit} onClick={() => onGo("deliveries")} testid="emp-stat-transit" />
      </div>
      {home.devices?.length > 0 && (
        <div>
          <div className="text-[12px] font-medium text-[color:var(--st-text-muted)] mb-2">Your devices</div>
          <div className="space-y-2">{home.devices.map((d) => <DeviceRow key={d.id} d={d} />)}</div>
        </div>
      )}
    </div>
  );
}

function DeviceRow({ d }) {
  const covered = d.warranty_end ? new Date(d.warranty_end) >= new Date() : null;
  return (
    <div className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4" data-testid={`emp-device-${d.id}`}>
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-[color:var(--st-surface-alt)]"><Laptop className="h-5 w-5 text-[color:var(--st-text-muted)]" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[color:var(--st-text)] truncate">{d.product || d.model}</div>
          <div className="text-xs text-[color:var(--st-text-subtle)]">{d.serial}{d.status ? ` · ${d.status}` : ""}</div>
        </div>
        {covered != null && (
          <span className={"text-[10px] font-semibold px-2 py-0.5 rounded-full " + (covered ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600")}>
            {covered ? "In warranty" : "Out of warranty"}
          </span>
        )}
      </div>
      {(d.warranty_end || d.applecare_end || d.assignment_date) && (
        <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-[color:var(--st-text-subtle)]">
          {d.assignment_date && <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Assigned {fmtDate(d.assignment_date)}</span>}
          {d.warranty_end && <span>Warranty {fmtDate(d.warranty_end)}</span>}
          {d.applecare_end && <span>AppleCare {fmtDate(d.applecare_end)}</span>}
        </div>
      )}
    </div>
  );
}

function useList(path) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(path).then(({ data }) => setItems(data.items || [])).catch(() => setItems([])), [path]);
  useEffect(() => { load(); }, [load]);
  return [items, load];
}

function Devices() {
  const [items] = useList("/employee-hub/devices");
  if (!items) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (!items.length) return <Empty label="No devices are assigned to you yet." />;
  return <div className="space-y-2" data-testid="emp-devices-list">{items.map((d) => <DeviceRow key={d.id} d={d} />)}</div>;
}

function Deliveries() {
  const [items, load] = useList("/employee-hub/deliveries");
  const confirm = async (id) => {
    try { await api.post(`/employee-hub/deliveries/${id}/confirm`); toast.success("Delivery confirmed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm"); }
  };
  if (!items) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (!items.length) return <Empty label="No deliveries for you right now." />;
  return (
    <div className="space-y-2" data-testid="emp-deliveries-list">
      {items.map((s) => {
        const delivered = (s.status || "").toUpperCase() === "DELIVERED";
        return (
          <div key={s.id} className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4" data-testid={`emp-delivery-${s.id}`}>
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-[color:var(--st-surface-alt)]"><Truck className="h-5 w-5 text-[color:var(--st-text-muted)]" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[color:var(--st-text)] truncate">{s.product || s.reference || "Shipment"}</div>
                <div className="text-xs text-[color:var(--st-text-subtle)]">{[s.courier, s.awb].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              <span className={"text-[11px] rounded-full px-2 py-0.5 " + (delivered ? "bg-emerald-100 text-emerald-700" : "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-muted)]")}>{(s.status || "").replace(/_/g, " ")}</span>
            </div>
            <div className="mt-2 flex items-center gap-3">
              {s.tracking_url && <a href={s.tracking_url} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--st-accent)] hover:underline">Track shipment →</a>}
              {!delivered && <Button size="sm" variant="outline" data-testid={`emp-confirm-${s.id}`} onClick={() => confirm(s.id)}>Confirm delivery</Button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Documents() {
  const [items] = useList("/employee-hub/documents");
  if (!items) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (!items.length) return <Empty label="No documents shared with you yet." />;
  return (
    <div className="space-y-2" data-testid="emp-documents-list">
      {items.map((d) => (
        <div key={d.id} className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4 flex items-center gap-3" data-testid={`emp-doc-${d.id}`}>
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-[color:var(--st-surface-alt)]"><PackageCheck className="h-5 w-5 text-[color:var(--st-text-muted)]" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[color:var(--st-text)] truncate">{d.name}</div>
            <div className="text-xs text-[color:var(--st-text-subtle)]">{[d.source, d.ref].filter(Boolean).join(" · ")}</div>
          </div>
          {d.url && <a href={(d.url.startsWith("http") ? d.url : (process.env.REACT_APP_BACKEND_URL + "/api" + d.url))} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--st-accent)] hover:underline">Open →</a>}
        </div>
      ))}
    </div>
  );
}

function Support({ employeeId, tenantId }) {
  const [items, load] = useList("/employee-hub/requests");
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3" data-testid="emp-support">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Your requests</div>
        <Button size="sm" data-testid="emp-raise-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> Report an issue</Button>
      </div>
      {!items ? <Loader2 className="h-4 w-4 animate-spin" />
        : items.length === 0 ? <Empty label="You have no requests yet." />
          : items.map((t) => (
            <div key={t.id} className="rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4" data-testid={`emp-request-${t.ticket_number}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-[color:var(--st-text-subtle)]">{t.ticket_number}</span>
                <span className="text-[11px] rounded-full bg-[color:var(--st-surface-alt)] px-2 py-0.5 text-[color:var(--st-text-muted)]">{(t.portal_status || "").replace(/_/g, " ")}</span>
              </div>
              <div className="mt-1 font-medium text-[color:var(--st-text)]">{t.subject}</div>
              <div className="text-xs text-[color:var(--st-text-subtle)]">{[t.ticket_type?.replace(/_/g, " "), t.serial].filter(Boolean).join(" · ")}</div>
            </div>
          ))}
      {open && <RaiseDrawer employeeId={employeeId} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

const REQ_TYPES = [
  ["INCIDENT", "Report an issue"], ["ACCESSORY_REQUEST", "Request accessory"],
  ["REPLACEMENT", "Request replacement"], ["PICKUP", "Request pickup"],
  ["OFFBOARDING_RECOVERY", "Return device"], ["OTHER", "Other request"],
];
const inputCls = "w-full rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[color:var(--st-accent)]";

function RaiseDrawer({ employeeId, onClose, onDone }) {
  const [f, setF] = useState({ ticket_type: "INCIDENT", subject: "", description: "", serial: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (f.subject.trim().length < 3 || f.description.trim().length < 3) { toast.error("Please add a subject and description"); return; }
    setBusy(true);
    try {
      await api.post("/service-desk/portal/requests", { ...f, employee_id: employeeId });
      toast.success("Request submitted");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="emp-raise-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[color:var(--st-surface)] h-full overflow-y-auto p-5 space-y-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">New request</h2>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]"><X className="h-4 w-4" /></button>
        </div>
        <label className="block"><span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">What do you need?</span>
          <select className={"mt-1 " + inputCls} data-testid="emp-req-type" value={f.ticket_type} onChange={(e) => set("ticket_type", e.target.value)}>
            {REQ_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></label>
        <label className="block"><span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Subject *</span>
          <input className={"mt-1 " + inputCls} data-testid="emp-req-subject" value={f.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Short summary" /></label>
        <label className="block"><span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Details *</span>
          <textarea className={"mt-1 " + inputCls} rows={4} data-testid="emp-req-desc" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell us what's happening" /></label>
        <label className="block"><span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Device serial (optional)</span>
          <input className={"mt-1 " + inputCls} data-testid="emp-req-serial" value={f.serial} onChange={(e) => set("serial", e.target.value)} /></label>
        <Button className="w-full" onClick={submit} disabled={busy} data-testid="emp-req-submit">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Submit request</Button>
      </div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="rounded-xl border border-dashed border-[color:var(--st-border)] p-8 text-center text-sm text-[color:var(--st-text-muted)]" data-testid="emp-empty">{label}</div>;
}
